import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const CACHE_EXPIRY_MS = 30 * 60 * 1000

export type PayoutInvoice = {
  invoiceId: string
  avatar: string | null
  amount: number
  status: string
  date: string // ISO
  invoiceNmbr: number
}

type PayoutsCacheEntry = {
  invoices: PayoutInvoice[]
  lastFetched: number | null
}

interface PayoutsCachePersistedState {
  entries: Record<string, PayoutsCacheEntry>
}

interface PayoutsCacheState extends PayoutsCachePersistedState {
  // helpers
  makeKey: (search: string) => string
  isStale: (key: string, now?: number) => boolean

  // getters
  getInvoices: (search: string) => PayoutInvoice[]
  getLastFetched: (search: string) => number | null

  // setters
  setInvoices: (search: string, invoices: PayoutInvoice[]) => void
  setLastFetched: (search: string, ts: number) => void

  // clearing
  clearKey: (search: string) => void
  clearAll: () => void
}

export const usePayoutsCacheStore = create<PayoutsCacheState>()(
  persist(
    (set, get) => ({
      entries: {},

      makeKey: (search: string) => `search:${(search ?? '').trim().toLowerCase()}`,

      isStale: (key: string, now = Date.now()) => {
        const entry = get().entries[key]
        if (!entry?.lastFetched) return true
        return now - entry.lastFetched > CACHE_EXPIRY_MS
      },

      getInvoices: (search: string) => {
        const key = get().makeKey(search)
        return get().entries[key]?.invoices ?? []
      },

      getLastFetched: (search: string) => {
        const key = get().makeKey(search)
        return get().entries[key]?.lastFetched ?? null
      },

      setInvoices: (search: string, invoices: PayoutInvoice[]) => {
        const key = get().makeKey(search)
        set(state => ({
          entries: {
            ...state.entries,
            [key]: {
              invoices,
              lastFetched: state.entries[key]?.lastFetched ?? null,
            },
          },
        }))
      },

      setLastFetched: (search: string, ts: number) => {
        const key = get().makeKey(search)
        set(state => ({
          entries: {
            ...state.entries,
            [key]: {
              invoices: state.entries[key]?.invoices ?? [],
              lastFetched: ts,
            },
          },
        }))
      },

      clearKey: (search: string) => {
        const key = get().makeKey(search)
        set(state => {
          const next = { ...state.entries }
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete next[key]
          return { entries: next }
        })
      },

      clearAll: () => set({ entries: {} }),
    }),
    {
      name: 'payouts-cache-v1',
      partialize: (state): PayoutsCachePersistedState => ({
        entries: state.entries,
      }),
    }
  )
)
